<template>
  <v-container fluid>
    <v-row align="center" justify="end" class="mx-2 mb-3">
      <v-col md="6">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Filtrar"
          single-line
          hide-details
        ></v-text-field>
      </v-col>
      <v-col md="6">
        <v-row align="center" justify="end" class="mx-2 mb-3">
          <span class="font-weight-bold grey--text">
            Total de Directivos activos: {{ activeDirectivesCount }}
          </span>
        </v-row>
      </v-col>
    </v-row>
    <v-data-table
      :search="search"
      :headers="headers"
      :items="list"
      item-key="id"
      class="elevation-0"
      :items-per-page="itemsPerPage"
      >
      <template v-slot:item.id="{ item }">
        <router-link :to="`/edit/directive-admins/${item.id}`">{{item.id}}</router-link>
      </template>
      <template v-slot:item.creationDate="{ item }">
        <span v-if="item.creationDate" class="font-weight-bold">{{ item.creationDate | moment('MM/D/YYYY') }}</span>
        <span v-else class="orange--text darken-2 font-weight-bold">{{ $t('directive_admins.empty') }}</span>
      </template>
      <template v-slot:item.active="{ item }">
        <span v-if="item.active"
              class="green--text font-weight-bold">{{$t('directive_admins.active')}}</span>
        <span v-else
              class="orange--text darken-2 font-weight-bold">{{$t('directive_admins.inactive')}}</span>
      </template>
      <template v-slot:item.impersonate="{ item }">
        <v-btn icon :title="$t('directive_admins.impersonate')" @click="openImpersonateModal(item)">
          <v-icon>mdi-account-switch</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Impersonate App Selector Modal -->
    <general-modal v-if="isImpersonateModalOpen" @onClose="closeImpersonateModal">
      <impersonate-app-selector-modal
        :user-name="impersonateUserLabel"
        @onSelect="handleImpersonateAppSelect"
        @onCancel="closeImpersonateModal"
      />
    </general-modal>
  </v-container>
</template>

<script>
import GeneralModal from '@/components/core/modals/GeneralModalContainer.vue';
import ImpersonateAppSelectorModal from '../../teachers/modals/ImpersonateAppSelectorModal.vue';
import LoadingMixin from '../../../mixins/loading';
import SnackbarMixin from '../../../mixins/snackbar';
import impersonationService from '../../../services/impersonation.service';
import {
  buildImpersonationLabel,
  buildImpersonationPayload,
  getImpersonationErrorMessage,
} from '../../../utils/impersonation';

export default {
  name: 'CollegeCourseDirectivesTable',
  components: {
    GeneralModal,
    ImpersonateAppSelectorModal,
  },
  props: {
    list: { type: Array, default: () => [] },
    value: { type: [String, Number], default: '' },
    pageCount: { type: [String, Number], default: '' },
    countryId: { type: [String, Number], default: '' },
    schoolId: { type: [Number, String], default: '' },
  },
  data: () => ({
    search: '',
    itemsPerPage: 15,
    isImpersonateModalOpen: false,
    userToImpersonate: null,
  }),
  computed: {
    activeDirectivesCount() {
      return this.list.filter((directive) => directive.active === true).length;
    },
    impersonateUserLabel() {
      return this.userToImpersonate ? buildImpersonationLabel(this.userToImpersonate) : '';
    },
    headers() {
      return [
        { text: 'ID', value: 'id' },
        { text: this.$t('directive_admins.firstname'), value: 'firstname', sortable: true },
        { text: this.$t('directive_admins.lastname'), value: 'lastname', sortable: true },
        { text: this.$t('directive_admins.email'), value: 'email' },
        { text: 'DNI', value: 'dni' },
        { text: this.$t('directive_admins.creation_date'), value: 'creationDate', dataType: 'Date' },
        { text: this.$t('directive_admins.state'), value: 'active' },
        { text: this.$t('directive_admins.impersonate'), value: 'impersonate', sortable: false },
      ];
    },
  },
  methods: {
    openImpersonateModal(user) {
      const payload = buildImpersonationPayload(user);
      if (!payload) {
        this.snackbarError(this.$t('students.action_error'));
        return;
      }
      this.userToImpersonate = user;
      this.isImpersonateModalOpen = true;
    },
    closeImpersonateModal() {
      this.isImpersonateModalOpen = false;
      this.userToImpersonate = null;
    },
    async handleImpersonateAppSelect(app) {
      const user = this.userToImpersonate;
      this.closeImpersonateModal();
      await this.impersonateUser(user, app === 'teachers');
    },
    async impersonateUser(user, useTeachersApp) {
      const payload = buildImpersonationPayload(user);
      if (!payload) {
        this.snackbarError(this.$t('students.action_error'));
        return;
      }
      this.loader();
      try {
        if (useTeachersApp) {
          const response = await impersonationService.impersonate(payload);
          const redirectURL = response?.content?.redirectURL;
          if (!redirectURL) throw new Error(String(response?.content ?? this.$t('students.action_error')));
          window.open(redirectURL, '_blank');
        } else {
          const response = await impersonationService.impersonateForDataSystem(payload);
          if (!response?.redirectURL) throw new Error(this.$t('students.action_error'));
          window.open(response.redirectURL, '_blank');
        }
        this.snackbarSuccess(`${this.$t('students.impersonate_opening')} ${buildImpersonationLabel(user)}`.trim());
      } catch (error) {
        this.snackbarError(getImpersonationErrorMessage(error, this.$t('students.action_error')));
      } finally {
        this.loader();
      }
    },
  },
  mixins: [LoadingMixin, SnackbarMixin],
};
</script>
